import './pdfUploader.css';
import React, { forwardRef, useImperativeHandle, useState, useRef, useMemo } from 'react';
import { getStorage, ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { useApiData } from '../../../../../../contexts/apiService';

export interface PdfUploaderHandle {
    openModal: () => void;
}

const PdfUploader = forwardRef<PdfUploaderHandle>((_props, forwardedRef) => {

    const [isModalOpen, setModalOpen] = useState(false);
    const [files, setFiles] = useState<File[]>([]);
    const [uploadProgress, setUploadProgress] = useState<{ [key: string]: number }>({});
    const [uploadStatus, setUploadStatus] = useState<{ [key: string]: string }>({});
    const [isDragging, setIsDragging] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const { apiData, pdfUploaded } = useApiData()
    useImperativeHandle(forwardedRef, () => ({ openModal }));

    // Helper to reset uploader state
    const resetUploader = () => {
        setFiles([]);
        setUploadProgress({});
        setUploadStatus({});
    };

    const openModal = () => {
        resetUploader();
        setModalOpen(true);
    };

    const closeModal = () => {
        setModalOpen(false);
        resetUploader();
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            const selectedFiles = Array.from(e.target.files);
            setFiles(selectedFiles);
            // Reset progress and status for each file
            const progressObj: { [key: string]: number } = {};
            const statusObj: { [key: string]: string } = {};
            selectedFiles.forEach(file => {
                progressObj[file.name] = 0;
                statusObj[file.name] = '';
            });
            setUploadProgress(progressObj);
            setUploadStatus(statusObj);
        }
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);
    
        if (e.dataTransfer.files) {
            const droppedFiles = Array.from(e.dataTransfer.files).filter(
                file => file.type === 'application/pdf'
            );
            if (droppedFiles.length > 0) {
                handleFileChange({ target: { files: droppedFiles } } as any);
            }
        }
    };

    const uniqueProducers = useMemo(() => {
        if (apiData?.lease_coordinates) {
            return Array.from(new Set(
                Object.values(apiData.lease_coordinates).map((item: any) => item.producer_name)
            ));
        }
        return [];
    }, [apiData]);

    const handleUpload = () => {
        if (files.length === 0) return;
        const storage = getStorage();
        
        let finishedCount = 0;
        let successCount = 0;
        const totalFiles = files.length;
        
        const checkAllFinished = () => {
            finishedCount++;
            if (finishedCount === totalFiles && successCount > 0) {
                pdfUploaded();
            }
        };
    
        files.forEach(file => {
            // Validate file name: remove extension, split on "_" and check if parts length is 3 or 5.
            const fileNameWithoutExt = file.name.replace(/\.[^/.]+$/, "");
            const parts = fileNameWithoutExt.split("_");
            if (parts.length !== 3 && parts.length !== 5) {
                setUploadStatus(prev => ({ ...prev, [file.name]: 'Invalid file name format' }));
                checkAllFinished();
                return; // Skip upload for this file.
            }
            
            // Determine the producer term: if 5 parts, use the 4th; if 3 parts, use the 3rd.
            const fileProducer = parts.length === 5 ? parts[3] : parts[2];
            if (!uniqueProducers.includes(fileProducer)) {
                setUploadStatus(prev => ({ ...prev, [file.name]: 'Wrong producer name' }));
                checkAllFinished();
                return;
            }
            
            const storageRef = ref(storage, `unassigned_tickets/${file.name}`);
            const uploadTask = uploadBytesResumable(storageRef, file);
        
            uploadTask.on(
                'state_changed',
                snapshot => {
                    const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                    setUploadProgress(prev => ({ ...prev, [file.name]: progress }));
                },
                error => {
                    console.error(`Error uploading ${file.name}:`, error);
                    setUploadStatus(prev => ({ ...prev, [file.name]: 'Error during upload' }));
                    checkAllFinished();
                },
                () => {
                    getDownloadURL(uploadTask.snapshot.ref).then(url => {
                        setUploadStatus(prev => ({ ...prev, [file.name]: 'Upload complete!' }));
                        successCount++;
                        checkAllFinished();
                    });
                }
            );
        });
    };

    return (
        <div>
            {isModalOpen && (
                <div className="pdf-modal-backdrop" onClick={closeModal}>
                    <div className="pdf-modal-content" onClick={e => e.stopPropagation()}>
                        {/* Fixed Header */}
                        <div className="pdf-modal-header">
                            <button 
                                className="close-button" 
                                onClick={closeModal}
                                style={{
                                    position: 'absolute',
                                    top: '10px',
                                    right: '10px',
                                    background: 'transparent',
                                    border: 'none',
                                    fontSize: '1rem',
                                    cursor: 'pointer',
                                    color: 'red',
                                }}
                            >
                                Close
                            </button>
                            <h2>Upload PDF Files</h2>
                        </div>

                        {/* Scrollable Content */}
                        <div className="pdf-modal-body">
                            <div 
                                className={`upload-area ${isDragging ? 'drag-active' : ''}`}
                                onClick={() => fileInputRef.current?.click()}
                                onDragOver={handleDragOver}
                                onDragLeave={handleDragLeave}
                                onDrop={handleDrop}
                                style={{ cursor: 'pointer' }}
                            >
                                <p>Drag PDFs here or click to select files</p>
                                <input
                                    type="file"
                                    accept="application/pdf"
                                    multiple
                                    onChange={handleFileChange}
                                    ref={fileInputRef}
                                    style={{ display: 'none' }}
                                    id="file-input"
                                />
                            </div>

                            <div className="file-list">
                                {files.map(file => {
                                    const statusText = uploadStatus[file.name];
                                    const isError =
                                        statusText === 'Invalid file name format' ||
                                        statusText === 'Wrong producer name';
                                    return (
                                        <div key={file.name} className="file-item">
                                            <div>{file.name}</div>
                                            <div className="progress-bar">
                                                <div 
                                                    className="progress-fill"
                                                    style={{ width: `${uploadProgress[file.name] || 0}%` }}
                                                />
                                            </div>
                                            <div className={`status ${isError ? 'error' : ''}`}>
                                                {uploadProgress[file.name] ? `${uploadProgress[file.name]}%` : '0%'}
                                                {statusText && ` - ${statusText}`}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Fixed Footer */}
                        <div className="pdf-modal-footer">
                            {files.length > 0 && (
                                <button className="upload-button" onClick={handleUpload}>
                                    Upload Files
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
});

export default PdfUploader;
